import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { PersonnelModal } from '../components/PersonnelModal';
import { personnelApi } from '../personnelApi';
import {
  EMPLOYMENT_TYPE_LABELS,
  PERSONNEL_AUDIT_ENTITY_TYPES,
  SEX_LABELS,
  type PersonnelListItem,
} from '../personnelTypes';
import { personDisplayName, usePersonNames, type PersonNames } from '../usePersonNames';

/**
 * The people employed against the plantilla.
 *
 * **The anchor column shows a name this list never fetched.** Rows arrive carrying a
 * `personId`; the page resolves the ids on screen in one batch call and the column reads the
 * answer. That is the composition §4.2 prescribes, and it is why the column is neither
 * sortable nor searchable — the values are in another database, so the server cannot order
 * or match on them. Search is by employee number, and the sort allow-list says so.
 */
export function PersonnelIndexPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PersonnelListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    personnel: PersonnelListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    The page holds the ids of the rows currently rendered so it can resolve them. `<IndexPage>`
    owns the fetch, so the list is captured on the way past rather than lifted out of the
    shell's state — which keeps the shell unaware that this module composes two systems.
  */
  const [pageIds, setPageIds] = useState<string[]>([]);
  const names = usePersonNames(pageIds);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    WHAT THIS MODULE CANNOT BE USED WITHOUT — one vacant post in the staffing pattern.

    The create wizard's Employment step asks for a position and the slot inside it, both
    required and both fed by the SAME vacancy feed, so an empty staffing pattern is not a thin
    form: it is a form nobody can submit. Without this the reader finds that out the long way —
    they press Create, fill in a name, a birth date and a contact number, reach the position
    picker with nothing in it, and the only thing on screen is a line of help text inside a
    modal they had to open to read.

    ONE entry, because the staffing pattern is the only catalog the form REQUIRES. Blood types,
    nationalities, religions and the identification types are optional pickers the reader skips,
    and the account step saves perfectly well on `skip`. The post in turn needs a position and
    an org unit — but each page states its OWN requirement and links to it, so the chain walks
    itself rather than this page naming three catalogs it holds no key for.

    Seeded TRUE and a failed feed leaves it true: flashing "set up the plantilla first" on every
    load of a working module is worse than a banner arriving a moment late, and a 403 — which is
    what a reader without `plantilla.read` gets — is not evidence that the plantilla is empty.
  */
  const [hasVacantPost, setHasVacantPost] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .vacantItems(controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setHasVacantPost(items.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();

    // Re-asked after a write, so a reader who adds the post and comes back finds the banner
    // gone without reloading the page.
  }, [reloadKey]);

  const fetchPersonnel = useCallback(async (query: IndexQueryState, signal: AbortSignal) => {
    const page = await personnelApi.list(
      {
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sort: query.sort ?? undefined,
        status: query.facets.status?.join(','),
        unit: query.facets.unit?.join(','),
        employment: query.facets.employment?.join(','),
        post: query.facets.post?.join(','),
      },
      signal,
    );

    /*
      Only the ids that came back. A record above this reader's access level is sent WITHOUT
      its personId — that absence is the mask — so there is nothing to resolve, and asking
      would be asking for a name the server has already refused.
    */
    setPageIds(page.items.map((p) => p.personId).filter((id): id is string => id != null));

    return page;
  }, []);

  const columns = useMemo(() => buildColumns(names), [names]);

  const rowActions = useCallback(
    (personnel: PersonnelListItem): MenuItem[] => [
      {
        key: `restore-${personnel.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'personnel.delete',
        disabledReason: personnel.isActive ? 'This record is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', personnel }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await personnelApi.remove(confirming.personnel.id);
      else await personnelApi.restore(confirming.personnel.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PersonnelListItem>
        module="personnel"
        breadcrumb={['Human Resources', 'Personnel']}
        columns={columns}
        facets={[
          { key: 'status', label: 'Status' },
          /*
            Unit and post are read through the staffing pattern — another module's
            records — so both carry its key. Employment type is this module's own field, where
            `personnel.read` already covers it; gating that one too would hide a filter over
            rows the reader is looking at right now.
          */
          { key: 'unit', label: 'Unit', permission: 'org-units.read' },
          { key: 'employment', label: 'Employment' },
          { key: 'post', label: 'Post', permission: 'plantilla.read' },
        ]}
        /*
          The parent AND every child, from the one shared list — a 201 file's sections are
          child tables, so `['Personnel']` alone hid every write to any of them. Recording a
          survey answer was audited the whole time; the module's history just never asked for
          `PersonnelSurveyAnswer`.
        */
        auditEntityTypes={PERSONNEL_AUDIT_ENTITY_TYPES}
        rowKey={(personnel) => personnel.id}
        fetch={fetchPersonnel}
        reloadKey={reloadKey}
        /*
          The placeholder says what the search actually matches. "Search personnel" would
          promise the name, which is the one thing it cannot do — and a reader typing a
          surname into a box that finds nothing concludes the list is broken.
        */
        searchPlaceholder="Search employee numbers"
        emptyMessage="No personnel yet. Add the people employed against the plantilla."
        /*
          See `hasVacantPost`. `plantilla.read` is the key the destination needs — it is implied
          from `personnel.assign`, which is the key appointing somebody takes anyway, so a reader
          who could fill this form gets the link and one who could not is still told why Create
          is disabled.
        */
        prerequisites={[
          {
            /*
              The label is read in THREE slots — "Set up X first", "Go to X", and the sentence
              the shell writes — so it is the PAGE, not the thing being counted. Written as "a
              vacant post in the plantilla" it rendered *"Nothing has been added to a vacant post
              in the plantilla yet"* and a button promising to take the reader to one record.
            */
            label: 'the plantilla',
            met: hasVacantPost,
            /*
              `met` asks a NARROWER question than "is the catalog empty": a plantilla of forty
              FILLED posts fails it, and the shell's default sentence would then state something
              plainly untrue about a screen one click away. This says only what the check proves.
            */
            detail:
              'There is no vacant post to appoint anybody into, so the form cannot be completed.',
            to: '/plantilla',
            permission: 'plantilla.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="personnel.create"
        /*
          The anchor opens the profile rather than the edit form: a 201 file is the substance
          here, and a modal of ten fields is not it. `openPermission` is the read key, because
          that is what opening the page actually needs.
        */
        onRowOpen={(personnel) => navigate(`/personnel/${personnel.id}`)}
        openPermission="personnel.read"
        rowActions={rowActions}
        onEdit={(personnel) => setEditing(personnel)}
        editPermission="personnel.update"
        onDelete={(personnel) => setConfirming({ kind: 'delete', personnel })}
        deletePermission="personnel.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(personnel) =>
          personnel.isActive ? null : 'This record is already deleted.'
        }
        onClearData={() => personnelApi.clear()}
        clearDataPermission="personnel.clear-data"
        otherInfo={(personnel) => personnel}
      />

      {creating || editing !== null ? (
        <PersonnelModal
          open
          editingId={editing?.id ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={
            confirming.kind === 'delete' ? 'Delete personnel record' : 'Restore personnel record'
          }
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`personnel-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {personDisplayName(confirming.personnel, names)}{' '}
                    is kept and can be restored — deleting hides the record, it does not erase it.
                  </p>

                  {/*
                    The consequence worth stating before the tick: the post they hold is
                    vacated, which changes the plantilla's own numbers. A reader deleting a
                    record does not necessarily expect that.
                  */}
                  {confirming.personnel.itemNumber ? (
                    <p>
                      Their appointment to{' '}
                      <span className="font-mono">{confirming.personnel.itemNumber}</span> ends
                      today, and the item becomes vacant.
                    </p>
                  ) : null}
                </>
              ) : (
                <p>
                  The record is listed again. It does <strong>not</strong> return to the post it
                  held — that item may since have been filled, so appointing them is a separate
                  step.
                </p>
              )}

              {error ? (
                <p role="alert" className="text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/**
 * Columns are built per render because the anchor reads resolved names.
 *
 * The alternative — a module-level constant closing over nothing — cannot show a name at all,
 * since the value does not come from the row.
 */
function buildColumns(names: PersonNames): ColumnDef<PersonnelListItem>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      width: 260,
      flex: true,

      /*
        NOT sortable, and the shell renders a plain header for it rather than a dead button.
        The values live in `rsys_pii`; ordering by one would mean resolving the whole table
        before paging it. The server's sort allow-list is employeeNumber and createdAt, and
        offering a control the server would ignore reads as a broken control.
      */
      render: (personnel) => <TextCell value={personDisplayName(personnel, names)} />,
    },
    {
      key: 'employeeNumber',
      header: 'Employee no.',
      width: 150,
      sortable: true,
      summary: true,
      render: (personnel) => <TextCell value={personnel.employeeNumber} mono />,
    },
    {
      key: 'itemNumber',
      header: 'Item',
      width: 140,
      summary: true,
      // Read through the staffing pattern, so more privileged than the row carrying it.
      permission: 'plantilla.read',
      render: (personnel) => <TextCell value={personnel.itemNumber} mono />,
    },
    {
      key: 'positionTitle',
      header: 'Position',
      width: 220,
      summary: true,
      permission: 'plantilla.read',
      render: (personnel) => <TextCell value={personnel.positionTitle} />,
    },
    {
      key: 'orgUnitName',
      header: 'Unit',
      width: 200,
      permission: 'org-units.read',
      render: (personnel) => <TextCell value={personnel.orgUnitName} />,
    },
    {
      key: 'employmentType',
      header: 'Employment',
      width: 150,
      render: (personnel) => (
        <TextCell
          value={personnel.employmentType ? EMPLOYMENT_TYPE_LABELS[personnel.employmentType] : null}
        />
      ),
    },
    {
      key: 'sex',
      header: 'Sex',
      width: 100,
      render: (personnel) => <TextCell value={SEX_LABELS[personnel.sex]} />,
    },
    {
      /*
        THE CLASSIFICATION, and it is drawn for every row including the ones this reader cannot
        open. That is the point: a reader may know a file exists, know how many there are, and
        know WHY one is out of reach, without being able to read it.

        Ungated — the rung a record sits on is this module's own field, not a more privileged
        value borrowed from elsewhere, and withholding it would leave a masked name with no
        explanation beside it.

        An unclassified record renders an em dash through `<TextCell>`'s own absent-value rule
        rather than an invented word: the record has no level, which is a different statement
        from any rung the ladder defines.
      */
      key: 'accessLevelName',
      header: 'Access level',
      width: 150,
      render: (personnel) => <TextCell value={personnel.accessLevelName} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (personnel) => (
        <StatusText tone={personnel.isActive ? 'active' : 'inactive'}>
          {personnel.isActive ? 'active' : 'inactive'}
        </StatusText>
      ),
    },
  ];
}
